import { computed, ref } from "vue";

export function useDescription(formState) {
  const maxDescriptionLength = 3800;
  const encodedDescriptionLength = ref(0);

  const html_escape = (mystr) =>
    Array.from(mystr)
      .map((char) => {
        const code = char.charCodeAt(0);
        return code < 32 || code > 126 || ["'", '"', "<", "&"].includes(char)
          ? `&#${code};`
          : char;
      })
      .join("")
      .trim();

  const countDescriptionEncodedCharacter = () => {
    encodedDescriptionLength.value = html_escape(
      formState.description ?? "",
    ).length;
  };

  const isDescriptionTooShort = () =>
    (formState.description?.length ?? 0) < 300;

  function formatLastname(lastname) {
    return (lastname || "")
      .toLowerCase()
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  function createDescription(summary) {
    const { n_files, extensions, dimension } = summary;

    const now = new Date();
    const formattedDate = new Intl.DateTimeFormat("en-GB").format(now);

    const authorStrings = (formState.uploaders ?? []).map((author, index) => {
      const lastname = formatLastname(author.lastname);
      const firstname = String(author.name ?? "").trim();
      const email = String(author.vuo_email ?? "").trim();

      // FIRST AUTHOR
      if (index === 0) {
        if (email) {
          return ` ${firstname} ${lastname} (${email})`;
        }

        return `${firstname} ${lastname}`;
      }

      // OTHER AUTHORS
      let initial = "";
      if (firstname.length > 0) {
        initial = firstname.charAt(0).toUpperCase();
      }

      return `${lastname} ${initial}.`;
    });

    let stringify_author = "";

    if (authorStrings.length === 1) {
      stringify_author = authorStrings[0];
    } else if (authorStrings.length === 2) {
      stringify_author = authorStrings[0] + " and " + authorStrings[1];
    } else if (authorStrings.length > 2) {
      const allButLast = authorStrings.slice(0, -1).join(", ");
      const last = authorStrings[authorStrings.length - 1];

      stringify_author = allButLast + ", and " + last;
    }

    const stringify_extension =
      (extensions?.length ?? 0) > 1
        ? `${extensions.join(", ")} types`
        : `${extensions?.[0] ?? ""} type`;

    formState.description = `Dataset of files associated with a ${
      formState.upload_type
    } entitled "${formState.title}".
It was uploaded on ${formattedDate} by ${stringify_author}.
The name associated with this archive indicates the person who uploaded it, not necessarily the main author, curator, or principal investigator.
The data is hosted by Elettra Sincrotrone Trieste. The hosting repository allows for download and in certain cases provides visualisation and processing services.
The automated file description suggests ${dimension} (uncompressed) ${n_files} ${
      n_files > 1 ? "files" : "file"
    } including ${stringify_extension}.
This description was automatically generated by Enodo (https://enodo.elettra.eu/).
Its license is CC-BY-SA-4.0 unless defined otherwise.`;

    countDescriptionEncodedCharacter();
  }

  // Input handlers the template already uses
  const handleInput = (e) => {
    const el = e.target;
    const newValue = el.value;
    const encodedLen = html_escape(newValue).length;

    if (encodedLen <= maxDescriptionLength) {
      formState.description = newValue;
      encodedDescriptionLength.value = encodedLen;
    } else {
      // Reject extra input
      el.value = formState.description ?? "";
    }
  };

  const handleKeydown = (e) => {
    const isSpecialKey =
      [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Tab",
      ].includes(e.key) ||
      e.ctrlKey ||
      e.metaKey;

    if (
      encodedDescriptionLength.value >= maxDescriptionLength &&
      !isSpecialKey
    ) {
      e.preventDefault();
    }
    countDescriptionEncodedCharacter();
  };

  return {
    maxDescriptionLength,
    encodedDescriptionLength,
    createDescription,
    isDescriptionTooShort,
    handleInput,
    handleKeydown,
    countDescriptionEncodedCharacter,
  };
}
